import React, { useState } from 'react';
import { Filter, Calendar, Search, X, ChevronDown, ChevronUp, Sliders, DollarSign } from 'lucide-react';

const ExpenseFilters = ({ filters, onFilterChange, onReset, expenses }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Extract unique values for filters
  const uniqueCategories = [...new Set(expenses.map(e => e.category))];
  const uniqueSubCategories = [...new Set(expenses.map(e => e.subCategory))];
  const uniqueVendors = [...new Set(expenses.map(e => e.vendorName).filter(Boolean))];
  const uniqueStaff = [...new Set(expenses.map(e => e.staffName).filter(Boolean))];
  const uniquePaymentModes = [...new Set(expenses.map(e => e.paymentMode))];

  const handleInputChange = (name, value) => {
    onFilterChange({ [name]: value });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      shop_expense: 'Shop Expense',
      purchase_invoice: 'Purchase Invoice',
      staff_payment: 'Staff Payment',
      others: 'Others'
    };
    return labels[category] || category;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl">
            <Filter className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <p className="text-sm text-gray-500">Narrow down your expense records</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <Sliders className="w-4 h-4" />
            <span>{showAdvanced ? 'Hide' : 'Advanced'}</span>
          </button>
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all"
          >
            <X className="w-4 h-4" />
            <span>Clear All</span>
          </button>
        </div>
      </div>

      {/* Basic Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Search className="w-4 h-4" />
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleInputChange('search', e.target.value)}
              placeholder="Search expenses..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <div className="relative">
            <select
              value={filters.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <div className="relative">
            <select
              value={filters.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
            >
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Payment Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Mode
          </label>
          <div className="relative">
            <select
              value={filters.paymentMode}
              onChange={(e) => handleInputChange('paymentMode', e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
            >
              <option value="">All Modes</option>
              {uniquePaymentModes.map(mode => (
                <option key={mode} value={mode}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1).replace('_', ' ')}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="space-y-4 mb-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date Range
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <span className="self-center text-gray-500">to</span>
                <div className="flex-1">
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Sub Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sub Category
              </label>
              <div className="relative">
                <select
                  value={filters.subCategory}
                  onChange={(e) => handleInputChange('subCategory', e.target.value)}
                  className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                >
                  <option value="">All Sub Categories</option>
                  {uniqueSubCategories.map(sub => (
                    <option key={sub} value={sub}>
                      {sub.charAt(0).toUpperCase() + sub.slice(1)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Vendor/Staff/Amount Range */}
            <div>
              {filters.category === 'purchase_invoice' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vendor Name
                  </label>
                  <div className="relative">
                    <select
                      value={filters.vendorName}
                      onChange={(e) => handleInputChange('vendorName', e.target.value)}
                      className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">All Vendors</option>
                      {uniqueVendors.map(vendor => (
                        <option key={vendor} value={vendor}>
                          {vendor}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </>
              ) : filters.category === 'staff_payment' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Staff Name
                  </label>
                  <div className="relative">
                    <select
                      value={filters.staffName}
                      onChange={(e) => handleInputChange('staffName', e.target.value)}
                      className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">All Staff</option>
                      {uniqueStaff.map(staff => (
                        <option key={staff} value={staff}>
                          {staff}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Amount Range
                  </label>
                  <div className="relative">
                    <select
                      value={filters.amountRange}
                      onChange={(e) => handleInputChange('amountRange', e.target.value)}
                      className="w-full appearance-none pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">All Amounts</option>
                      <option value="0-5000">Up to ₹5,000</option>
                      <option value="5000-20000">₹5,000 - ₹20,000</option>
                      <option value="20000-50000">₹20,000 - ₹50,000</option>
                      <option value="50000+">Above ₹50,000</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      <div className="pt-6 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium text-gray-700">Active Filters:</span>
          <span className="text-sm text-gray-500">({Object.values(filters).filter(f => f).length} applied)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(filters).map(([key, value]) => {
            if (!value || key === 'search') return null;
            
            let displayValue = value;
            if (key === 'category') displayValue = getCategoryLabel(value);
            if (key === 'paymentMode') displayValue = value.charAt(0).toUpperCase() + value.slice(1).replace('_', ' ');
            if (key === 'status') displayValue = value.charAt(0).toUpperCase() + value.slice(1);
            if (key === 'amountRange') {
              const ranges = {
                '0-5000': 'Up to ₹5,000',
                '5000-20000': '₹5,000 - ₹20,000',
                '20000-50000': '₹20,000 - ₹50,000',
                '50000+': 'Above ₹50,000'
              };
              displayValue = ranges[value] || value;
            }
            
            return (
              <span
                key={key}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-4 py-2 rounded-full text-sm border border-green-200"
              >
                <span className="font-medium">
                  {key.replace(/([A-Z])/g, ' $1').toLowerCase().replace('start', 'from').replace('end', 'to')}:
                </span>
                <span className="font-semibold">{displayValue}</span>
                <button
                  onClick={() => handleInputChange(key, '')}
                  className="text-green-600 hover:text-green-800 p-0.5 hover:bg-green-100 rounded-full"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExpenseFilters;